import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';
import { useGetRecordsRequestStatusQuery } from '@/services/api/agentflowService';

import {
  getRecordsRequestAgentDisplayState,
  getRecordsRequestStepLabel,
  isNewRecordsRequestBackendShape,
  RECORDS_REQUEST_AGENT_TERMINAL_STATUSES,
} from './recordsRequestAgentState';
import { RecordsRequestEvaluationModal } from './RecordsRequestEvaluationModal';
import { SubmissionAttemptModal } from './submission-attempt';

const shimmerStyle = {
  background: 'linear-gradient(90deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.8) 40%, rgba(255,255,255,1) 50%, rgba(255,255,255,0.8) 60%, rgba(255,255,255,0.4) 100%)',
  backgroundSize: '200% 100%',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  animation: 'shimmer-sweep 2.5s ease-in-out infinite',
} as const;

export const RECORDS_REQUEST_EVAL_ALLOWED_EMAILS = new Set([
  'misty@finchlegal.com',
  'scott@finchlegal.com',
  'sid@finchlegal.com',
  'admin@finchlegal.com',
]);

interface RecordsRequestStatusLineProps {
  matterId: string;
  treatmentRequestId: string;
  /** Controlled modal state — parent opens this when the TaskActionButton is clicked */
  modalOpen?: boolean;
  onModalOpenChange?: (open: boolean) => void;
}

/**
 * Renders the running shimmer bar when the agent is active, and hosts
 * the submission attempt / legacy evaluation modals.
 *
 * The TaskActionButton label override is computed directly in TaskDetail
 * via useGetRecordsRequestStatusQuery — no callbacks needed.
 */
export const RecordsRequestStatusLine = ({
  matterId,
  treatmentRequestId,
  modalOpen: controlledModalOpen,
  onModalOpenChange,
}: RecordsRequestStatusLineProps) => {
  const [internalModalOpen, setInternalModalOpen] = useState(false);
  const [hasTerminalStatus, setHasTerminalStatus] = useState(false);

  const modalOpen = controlledModalOpen ?? internalModalOpen;
  const setModalOpen = onModalOpenChange ?? setInternalModalOpen;

  const { data } = useGetRecordsRequestStatusQuery(
    { matterId, requestId: treatmentRequestId },
    {
      pollingInterval: hasTerminalStatus ? undefined : 5000,
    },
  );

  useEffect(() => {
    if (!data) return;
    setHasTerminalStatus(
      RECORDS_REQUEST_AGENT_TERMINAL_STATUSES.has(data.status),
    );
  }, [data]);

  const displayState =
    data && data.status !== 'not_found'
      ? getRecordsRequestAgentDisplayState(data)
      : null;

  const useNewModal = data ? isNewRecordsRequestBackendShape(data) : false;
  const evaluationRunId = data?.evaluation_run_id;

  const stepLabel = getRecordsRequestStepLabel(data);

  return (
    <>
      {/* Running state: shimmer bar that replaces the TaskActionButton */}
      {displayState === 'running' && (
        <>
          <style>{`@keyframes shimmer-sweep { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
          <button
            type="button"
            disabled
            className="flex w-full items-center justify-center gap-2.5 rounded-xl bg-zinc-900 px-4 py-2 text-xs font-semibold text-white cursor-default h-8"
          >
            <span className={cn('inline-block h-2 w-2 shrink-0 rounded-full bg-indigo-400 animate-pulse')} />
            <span className="truncate" style={shimmerStyle}>
              {stepLabel ?? 'Preparing submission...'}
            </span>
          </button>
        </>
      )}

      {/* Submission attempt modal (new backend) */}
      {useNewModal && (
        <SubmissionAttemptModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          onOpenRequestModal={() => {}}
          matterId={matterId}
          treatmentRequestId={treatmentRequestId}
        />
      )}

      {/* Legacy evaluation modal (old backend) */}
      {!useNewModal && evaluationRunId && (
        <RecordsRequestEvaluationModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          matterId={matterId}
          treatmentRequestId={treatmentRequestId}
          evaluationRunId={evaluationRunId}
        />
      )}
    </>
  );
};
